import React, {useContext, useRef, useCallback} from 'react'
import {useTranslation} from 'react-i18next'
import {QRCodeCanvas} from 'qrcode.react'

import {QRCodeContainer} from './PublicServiceUrlModal.styled'

import {Typography} from '~/components/general/typography/Typography'
import {Button} from '~/components/general/button/Button'
import {
  NotificationContext,
  NotificationType,
} from '~/components/notifications/NotificationContext'
import {PartnerAdministratorContext} from '~/components/profile/PartnerAdministratorContext'

type ServiceQRCodeSectionProps = {
  url: string
}

const QR_SIZE = 200
const LOGO_SIZE = 40

export const ServiceQRCodeSection: React.FC<ServiceQRCodeSectionProps> = ({
  url,
}) => {
  const {t} = useTranslation()
  const {addNotification} = useContext(NotificationContext)
  const {partnerAdministrator} = useContext(PartnerAdministratorContext)
  const qrCanvasRef = useRef<HTMLCanvasElement>(null)

  const handleCopyQRCode = useCallback(async () => {
    const canvas = qrCanvasRef.current
    if (!canvas) return

    try {
      canvas.toBlob(async (blob) => {
        if (blob) {
          try {
            await navigator.clipboard.write([
              new ClipboardItem({'image/png': blob}),
            ])
            addNotification(t('Copied!'), NotificationType.Success)
          } catch {
            addNotification(
              t('Failed to copy QR code.'),
              NotificationType.Error,
            )
          }
        }
      }, 'image/png')
    } catch {
      addNotification(t('Failed to copy QR code.'), NotificationType.Error)
    }
  }, [addNotification, t])

  if (!url) return null

  return (
    <QRCodeContainer>
      <Typography weight="medium">{t('QR Code')}</Typography>
      <QRCodeCanvas
        ref={qrCanvasRef}
        value={url}
        size={QR_SIZE}
        level="M"
        marginSize={2}
        imageSettings={{
          src: partnerAdministrator?.partner.logoUrl || '/favicon.png',
          height: LOGO_SIZE,
          width: LOGO_SIZE,
          excavate: true,
          crossOrigin: 'anonymous',
        }}
      />
      <Button color="secondary" onClick={handleCopyQRCode}>
        {t('Copy QR code')}
      </Button>
    </QRCodeContainer>
  )
}
